import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import Backdrop from 'components/Backdrop';
import TestWelcomeScreen from 'features/first_test/WelcomeScreen';
import TestTakingScreen from 'features/first_test/TestScreen';
import TestThankYouScreen from 'features/first_test/TestThankYouScreen';
import VideoWelcomeScreen from 'features/video_test/VideoWelcomeScreen';
import VideoTestScreen from 'features/video_test/VideoTestScreen';
import VideoThankYouScreen from 'features/video_test/VideoThankYouScreen';
import GeneralQuestionnaireWelcomeScreen from 'features/general_questionnaire/GeneralQuestionnaireWelcomeScreen';
import GeneralQuestionnaireScreen from 'features/general_questionnaire/GeneralQuestionnaireScreen';
import GeneralQuestionnaireThankYouScreen from 'features/general_questionnaire/GeneralQuestionnaireThankYouScreen';
import { getTestProgression } from 'api/apiHelper';

type TestStage = 'initial_welcome' | 'initial_test' | 'initial_test_thankyou' |
                 'video_welcome' | 'video_test' | 'video_test_result' |
                 'questionnaire_welcome' | 'questionnaire_test' |
                 'final_thankyou' | 'loading' | 'error';

interface TestProgression {
  initial_test: {
    status: string;
    test_token: string;
    completed: boolean;
  };
  video_test: {
    status: string;
    test_token: string;
    completed: boolean;
  } | null;
  questionnaire: {
    status: string;
    test_token: string;
    completed: boolean;
  } | null;
}

const UnifiedTestFlow: React.FC = () => {
  const { testToken } = useParams<{ testToken: string }>();
  const navigate = useNavigate();
  
  const [stage, setStage] = useState<TestStage>('loading');
  const [error, setError] = useState<string>('');
  const [progression, setProgression] = useState<TestProgression | null>(null);
  const progressionRef = useRef<TestProgression | null>(null);
  
  // Keep ref in sync with state
  useEffect(() => {
    progressionRef.current = progression;
  }, [progression]);

  const checkProgression = useCallback(async () => {
    try {
      setStage('loading');
      setError('');
      
      const response = await getTestProgression(testToken!);
      const progressionData: TestProgression = response.data;
      setProgression(progressionData);
      
      // Determine which stage to show
      if (!progressionData.initial_test.completed) {
        // Initial test not completed
        if (progressionData.initial_test.status === 'pending') {
          setStage('initial_welcome');
        } else if (progressionData.initial_test.status === 'in_progress') {
          setStage('initial_test');
        } else {
          setError('Initial test link has expired or is invalid');
          setStage('error');
        }
      } else if (!progressionData.video_test || !progressionData.video_test.completed) {
        // Initial test completed, check video test
        if (!progressionData.video_test) {
          // Video test not created yet - stay in loading state, polling will detect it
          setStage('loading');
          return;
        }
        if (progressionData.video_test.status === 'pending') {
          setStage('video_welcome');
        } else if (progressionData.video_test.status === 'in_progress') {
          setStage('video_test');
        } else {
          setError('Video test link has expired or is invalid');
          setStage('error');
        }
      } else if (!progressionData.questionnaire || !progressionData.questionnaire.completed) {
        // Video test completed, check questionnaire
        if (!progressionData.questionnaire) {
          // Questionnaire not created yet - stay in loading state, polling will detect it
          setStage('loading');
          return;
        }
        if (progressionData.questionnaire.status === 'pending') {
          setStage('questionnaire_welcome');
        } else if (progressionData.questionnaire.status === 'in_progress') {
          setStage('questionnaire_test');
        } else {
          setError('Questionnaire link has expired or is invalid');
          setStage('error');
        }
      } else {
        // All tests completed
        setStage('final_thankyou');
      }
    } catch (error: any) {
      console.error('Error checking progression:', error);
      setError(error.response?.data?.error || error.message || 'Failed to load test progression');
      setStage('error');
    }
  }, [testToken]);

  useEffect(() => {
    if (testToken) {
      checkProgression();
    }
  }, [testToken, checkProgression]);

  const handleInitialTestComplete = () => {
    // After initial test completion, show thank you screen
    setStage('initial_test_thankyou');
  };

  const handleContinueToVideoTest = async () => {
    // Check if video test session exists, wait for it if needed
    setStage('loading');
    let retries = 0;
    const maxRetries = 10;
    
    const checkForVideoTest = async (): Promise<void> => {
      try {
        const response = await getTestProgression(testToken!);
        const progressionData: TestProgression = response.data;
        setProgression(progressionData);
        
        if (progressionData.video_test) {
          // Video test created, navigate to video welcome
          if (progressionData.video_test.status === 'pending') {
            setStage('video_welcome');
          } else if (progressionData.video_test.status === 'in_progress') {
            setStage('video_test');
          } else {
            setError('Video test link has expired or is invalid');
            setStage('error');
          }
        } else if (retries < maxRetries) {
          retries++;
          setTimeout(checkForVideoTest, 1000);
        } else {
          setError('Video test is being prepared. Please wait a moment and refresh the page.');
          setStage('error');
        }
      } catch (error: any) {
        console.error('Error checking for video test:', error);
        setError('Failed to proceed to video test. Please try again.');
        setStage('error');
      }
    };
    
    checkForVideoTest();
  };

  const handleVideoTestComplete = () => {
    // After video test completion, show thank you screen
    setStage('video_test_result');
  };

  const handleContinueToQuestionnaire = async () => {
    // Check if questionnaire session exists, wait for it if needed
    setStage('loading');
    let retries = 0;
    const maxRetries = 10;
    
    const checkForQuestionnaire = async (): Promise<void> => {
      try {
        const response = await getTestProgression(testToken!);
        const progressionData: TestProgression = response.data;
        setProgression(progressionData);
        
        if (progressionData.questionnaire) {
          // Questionnaire created, navigate to questionnaire welcome
          if (progressionData.questionnaire.status === 'pending') {
            setStage('questionnaire_welcome');
          } else if (progressionData.questionnaire.status === 'in_progress') {
            setStage('questionnaire_test');
          } else {
            setError('Questionnaire link has expired or is invalid');
            setStage('error');
          }
        } else if (retries < maxRetries) {
          retries++;
          setTimeout(checkForQuestionnaire, 1000);
        } else {
          setError('Questionnaire is being prepared. Please wait a moment and refresh the page.');
          setStage('error');
        }
      } catch (error: any) {
        console.error('Error checking for questionnaire:', error);
        setError('Failed to proceed to questionnaire. Please try again.');
        setStage('error');
      }
    };
    
    checkForQuestionnaire();
  };

  const handleQuestionnaireComplete = () => {
    // After questionnaire completion, show final thank you
    setStage('final_thankyou');
  };

  if (stage === 'loading') {
    return <Backdrop message="Loading test..." />;
  }

  if (stage === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">Error</h2>
          <p className="text-center text-gray-600 mb-6">{error}</p>
        </div>
      </div>
    );
  }

  // Render appropriate component based on stage
  switch (stage) {
    case 'initial_welcome':
      return <TestWelcomeScreen testToken={testToken!} onTestStarted={checkProgression} />;
    
    case 'initial_test':
      return <TestTakingScreen onComplete={handleInitialTestComplete} testToken={testToken!} />;
    
    case 'initial_test_thankyou':
      return <TestThankYouScreen 
        onContinueToVideoTest={handleContinueToVideoTest} 
        isUnifiedFlow={true}
        initialTestToken={testToken!}
      />;
    
    case 'video_welcome':
      return progression?.video_test ? <VideoWelcomeScreen testToken={progression.video_test.test_token} onTestStarted={checkProgression} /> : <Backdrop message="Loading..." />;
    
    case 'video_test':
      return progression?.video_test ? <VideoTestScreen onComplete={handleVideoTestComplete} testToken={progression.video_test.test_token} /> : <Backdrop message="Loading..." />;
    
    case 'video_test_result':
      return progression?.video_test ? <VideoThankYouScreen 
        testToken={progression.video_test.test_token}
        onContinueToQuestionnaire={handleContinueToQuestionnaire}
        isUnifiedFlow={true}
      /> : <Backdrop message="Loading..." />;
    
    case 'questionnaire_welcome':
      return progression?.questionnaire ? <GeneralQuestionnaireWelcomeScreen testToken={progression.questionnaire.test_token} onTestStarted={checkProgression} /> : <Backdrop message="Loading..." />;
    
    case 'questionnaire_test':
      return progression?.questionnaire ? <GeneralQuestionnaireScreen onComplete={handleQuestionnaireComplete} testToken={progression.questionnaire.test_token} /> : <Backdrop message="Loading..." />;
    
    case 'final_thankyou':
      // Pass the initial test token for the thank you screen
      return <GeneralQuestionnaireThankYouScreen testToken={testToken!} />;
    
    default:
      return <Backdrop message="Loading..." />;
  }
};

export default UnifiedTestFlow;
